import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router';
import { AppTheme } from '../../settings/appTheme';
import { PagesRoutes, Routes } from '../../store/routes';

const POPUP_QUERY_KEY = 'landing';

type SlideContent = {
  id: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  description: React.ReactNode;
  showCta?: boolean;
};

const Highlight = styled.span`
  color: ${AppTheme.colors.accent};
  font-weight: bold;
`;

const BrandName = styled.span`
  color: ${AppTheme.colors.accent};
  font-family: ${AppTheme.fonts.blackCapsFont};
`;

const slides: SlideContent[] = [
  {
    id: 'welcome',
    title: (
      <>
        Welcome to
      </>
    ),
    description: (
      <>
        GET <Highlight>FREE TICKETS</Highlight> TO OVER <Highlight>1,000 CONCERTS</Highlight>
        <br />
        BY HELPING US <Highlight>DISTRIBUTE THEM</Highlight>
      </>
    ),
    subtitle: (
      <>
        <Highlight>EARN 10%</Highlight> ON ALL YOUR <Highlight>SALES</Highlight> AND <Highlight>CASH OUT</Highlight>
      </>
    ),
  },
  {
    id: 'earn',
    title: (
      <>
        
      </>
    ),
    description: (
      <>
        DISTRIBUTE TICKETS
        <br />
        EARN POINTS
        <br />
        <Highlight>SPEND POINTS ON TICKETS</Highlight>
        <br />
        OR <Highlight>CASH OUT</Highlight>
      </>
    ),
  },
  {
    id: 'join',
    title: (
      <>
        <Highlight>THOUSANDS</Highlight> OF USERS HAVE ALREADY <Highlight>EARNED MONEY</Highlight> AND GOT THEIR{' '}
        <Highlight>BONUS TICKETS</Highlight>.
        <br />
        <Highlight>GET STARTED NOW!</Highlight>
      </>
    ),
    description: <></>,
    showCta: true,
  },
];

export const LandingPopup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const hasPopupParam = searchParams.has(POPUP_QUERY_KEY);
  const [isOpen, setIsOpen] = useState(hasPopupParam);
  const [activeSlide, setActiveSlide] = useState(0);

  const isMainPage =
    location.pathname === PagesRoutes.Main ||
    location.pathname === Routes.Main ||
    location.pathname === Routes.Main.replace(/\/$/, '');

  useEffect(() => {
    setIsOpen(hasPopupParam);
  }, [hasPopupParam]);

  if (!isMainPage || !isOpen) {
    return null;
  }

  const slide = slides[activeSlide];

  const handleClose = () => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete(POPUP_QUERY_KEY);
    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
    setIsOpen(false);
  };

  const handlePrev = () => {
    setActiveSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const handleJoinClick = () => {
    handleClose();
    navigate(Routes.RegistrationV1);
  };

  const isFirstSlide = activeSlide === 0;
  const isLastSlide = activeSlide === slides.length - 1;

  const slideImages = [
    'content/img/landing/1 stage.jpg',
    'content/img/landing/2 stage.jpg',
    'content/img/landing/3 stage.png',
  ];

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <PopupWrapper>
        <PopupHeader>
          {isFirstSlide && <PopupHeaderTitle>{slide.title}</PopupHeaderTitle>}
          <Logo src={AppTheme.img.logo} alt="Fanaticka" />
        </PopupHeader>
        <PopupContainer>
          <ArrowButton type="button" aria-label="Previous slide" direction="left" onClick={handlePrev}>
            &lt;
          </ArrowButton>
          <Popup>
            {activeSlide === slides.length - 1 && (
              <CloseButton type="button" aria-label="Close" onClick={handleClose}>
                x
              </CloseButton>
            )}
            <PopupContent>
              {!isFirstSlide && <SlideTitle>{slide.title}</SlideTitle>}
              {isFirstSlide ? (
              <>
                <FirstSlideDescription>{slide.description}</FirstSlideDescription>
                <FirstSlideRow>
                  <PhoneMock $isLastSlide={isLastSlide}>
                    <ScreenshotImage 
                      src={slideImages[activeSlide]} 
                      alt={`Stage ${activeSlide + 1}`}
                      $isLastSlide={isLastSlide}
                    />
                  </PhoneMock>
                  <FirstSlideTextContainer>
                    {slide.subtitle && (
                      <>
                        <OrSeparator>OR</OrSeparator>
                        <FirstSlideText>{slide.subtitle}</FirstSlideText>
                      </>
                    )}
                  </FirstSlideTextContainer>
                </FirstSlideRow>
              </>
            ) : (
              <>
                <SlideDescription>{slide.description}</SlideDescription>
                <SlideBody>
                  <PhoneMock $isLastSlide={isLastSlide}>
                    <ScreenshotImage 
                      src={slideImages[activeSlide]} 
                      alt={`Stage ${activeSlide + 1}`}
                      $isLastSlide={isLastSlide}
                    />
                  </PhoneMock>
                </SlideBody>
                {slide.subtitle && <SlideSubtitle>{slide.subtitle}</SlideSubtitle>}
              </>
            )}
              {slide.showCta && (
                <JoinButton type="button" $isLastSlide={isLastSlide} onClick={handleJoinClick}>
                  JOIN FANATICKA NOW
                </JoinButton>
              )}
          </PopupContent>
          <Dots>
            {slides.map((item, index) => (
              <Dot
                key={item.id}
                type="button"
                aria-label={`Go to slide ${index + 1}`}
                isActive={index === activeSlide}
                onClick={() => setActiveSlide(index)}
              />
            ))}
          </Dots>
        </Popup>
          <ArrowButton type="button" aria-label="Next slide" direction="right" onClick={handleNext}>
            &gt;
          </ArrowButton>
        </PopupContainer>
      </PopupWrapper>
      </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 640px) {
    padding: 12px 8px;
    align-items: flex-start;
  }

  @media (max-height: 700px) {
    align-items: flex-start;
    padding-top: 12px;
  }
`;

const PopupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-height: min(calc(100vh - 48px), calc(100dvh - 48px));
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 640px) {
    gap: 8px;
    max-height: min(calc(100vh - 24px), calc(100dvh - 24px));
  }
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 12px;
  width: min(520px, 100%);
  padding-bottom: 4px;
  flex-shrink: 0;
  min-width: 0;

  @media (max-width: 640px) {
    gap: 6px;
    width: min(92vw, 100%);
    padding-bottom: 2px;
  }
`;

const PopupHeaderTitle = styled.div`
  font-family: ${AppTheme.fonts.regularFont};
  font-size: clamp(14px, 3.5vw, 18px);
  letter-spacing: 0.02em;
  text-transform: none;
  line-height: 1.25;
  font-weight: normal;
  color: ${AppTheme.colors.text};
  flex-shrink: 0;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const Logo = styled.img`
  height: 40px;
  max-width: min(45vw, 180px);
  object-fit: contain;
  flex-shrink: 1;
  min-width: 0;

  @media (max-width: 640px) {
    height: clamp(28px, 8vw, 36px);
  }
`;

const PopupContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: min(520px, 100%);
  border: 32px solid #000000;
  border-radius: 60px;
  padding: 0;
  flex-shrink: 1;
  min-width: 0;
  max-height: 100%;

  @media (max-width: 640px) {
    width: min(96vw, 100%);
    border-width: 12px;
    border-radius: 28px;
  }

  @media (max-width: 380px) {
    border-width: 8px;
    border-radius: 20px;
  }
`;

const Popup = styled.div`
  position: relative;
  width: 100%;
  background: #0b0b0b;
  border-radius: 28px;
  border: 2px solid ${AppTheme.colors.accent};
  padding: 28px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
  min-height: 0;
  overflow: hidden;

  @media (max-width: 640px) {
    border-radius: 16px;
    padding: 16px 12px 12px;
    gap: 10px;
  }

  @media (max-width: 380px) {
    border-radius: 12px;
    padding: 12px 10px 10px;
    gap: 8px;
  }
`;

const PopupContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  text-align: center;
  color: ${AppTheme.colors.text};
  position: relative;
  z-index: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1 1 auto;

  @media (max-width: 640px) {
    gap: 8px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 16px;
  background: transparent;
  color: #6f6f6f;
  border: none;
  font-size: 26px;
  cursor: pointer;
  line-height: 1;

  @media (max-width: 640px) {
    top: 8px;
    right: 10px;
    font-size: 20px;
  }
`;

const SlideTitle = styled.div`
  font-family: ${AppTheme.fonts.blackCapsFont};
  font-size: clamp(14px, 4vw, 22px);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.25;
  font-weight: normal;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const SlideSubtitle = styled.div`
  font-family: ${AppTheme.fonts.regularFont};
  font-size: clamp(12px, 3.5vw, 18px);
  line-height: 1.4;
  text-transform: uppercase;
  font-weight: normal;

  @media (max-width: 640px) {
    font-size: 12px;
  }
`;

const SlideDescription = styled.div`
  font-family: ${AppTheme.fonts.regularFont};
  font-size: clamp(12px, 3.5vw, 18px);
  line-height: 1.4;
  text-transform: uppercase;
  font-weight: normal;

  @media (max-width: 640px) {
    font-size: 12px;
  }
`;

const FirstSlideDescription = styled.div`
  font-family: ${AppTheme.fonts.regularFont};
  font-size: clamp(12px, 3.5vw, 18px);
  line-height: 1.4;
  text-transform: uppercase;
  font-weight: normal;
  text-align: center;
  width: 100%;

  @media (max-width: 640px) {
    font-size: 12px;
  }
`;

const SlideBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 6px;
  flex-shrink: 1;
  min-height: 0;

  @media (max-width: 640px) {
    margin-top: 4px;
  }
`;

const FirstSlideRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 2fr) minmax(120px, 1fr);
  gap: 20px;
  align-items: center;
  width: 100%;
  margin-top: 12px;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: 640px) {
    margin-top: 6px;
    gap: 10px;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const FirstSlideTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 640px) {
    gap: 6px;
  }
`;

const OrSeparator = styled.div`
  font-family: ${AppTheme.fonts.blackCapsFont};
  font-size: clamp(24px, 10vw, 48px);
  color: ${AppTheme.colors.accent};
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1;

  @media (max-width: 640px) {
    font-size: 24px;
  }

  @media (max-width: 380px) {
    font-size: 20px;
  }
`;

const FirstSlideText = styled(SlideSubtitle)`
  text-align: center;
  padding-top: 0;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const ArrowButton = styled.button<{ direction: 'left' | 'right' }>`
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border: none;
  font-size: 32px;
  cursor: pointer;
  padding: 8px 12px;
  opacity: 0.7;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1001;
  flex-shrink: 0;
  ${(props) => (props.direction === 'left' ? 'left: -35px;' : 'right: -35px;')}

  &:hover {
    opacity: 1;
    color: ${AppTheme.colors.text};
  }

  @media (max-width: 640px) {
    font-size: 24px;
    padding: 4px 8px;
    ${(props) => (props.direction === 'left' ? 'left: -24px;' : 'right: -24px;')}
  }

  @media (max-width: 480px) {
    ${(props) => (props.direction === 'left' ? 'left: -20px;' : 'right: -20px;')}
    font-size: 20px;
  }

  @media (max-width: 380px) {
    ${(props) => (props.direction === 'left' ? 'left: 4px;' : 'right: 4px;')}
    font-size: 18px;
  }
`;

const PhoneMock = styled.div<{ $isLastSlide?: boolean }>`
  width: 100%;
  max-width: min(72vw, 280px);
  aspect-ratio: 9 / 16;
  border-radius: 24px;
  background: transparent;
  ${(props) => !props.$isLastSlide && 'border: 1px solid rgba(255, 255, 255, 0.08);'}
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  flex-shrink: 1;
  min-width: 0;

  @media (max-width: 640px) {
    max-width: min(55vw, 200px);
    border-radius: 16px;
  }

  @media (max-width: 380px) {
    max-width: min(50vw, 160px);
    border-radius: 12px;
  }
`;

const ScreenshotImage = styled.img<{ $isLastSlide?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => (props.$isLastSlide ? 'contain' : 'cover')};
  border-radius: inherit;
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    gap: 6px;
  }
`;

const Dot = styled.button<{ isActive: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  border: none;
  background: ${(props) => (props.isActive ? AppTheme.colors.accent : 'rgba(255, 255, 255, 0.3)')};
  cursor: pointer;
  padding: 0;

  @media (max-width: 640px) {
    width: 6px;
    height: 6px;
  }
`;

const JoinButton = styled.button<{ $isLastSlide?: boolean }>`
  margin-top: 4px;
  padding: 12px 26px;
  border-radius: 999px;
  background: ${AppTheme.colors.accent};
  border: none;
  color: ${(props) => (props.$isLastSlide ? '#ffffff' : '#0b0b0b')};
  font-family: ${AppTheme.fonts.blackCapsFont};
  font-size: 16px;
  letter-spacing: 0.06em;
  cursor: pointer;
  flex-shrink: 0;

  @media (max-width: 640px) {
    padding: 10px 18px;
    font-size: 12px;
    margin-top: 2px;
  }

  @media (max-width: 380px) {
    padding: 8px 14px;
    font-size: 11px;
  }
`;
